import React from "react";
import { useKeyPressEvent } from "react-use";

import type { Time } from "~/tools/date-time/time";
import { batch, useScope, useScopedMemo } from "~/tools/impulse";
import { useHandler } from "~/tools/use-handler";

import { SpecificRange } from "../../../app/state/view/timeline/TimeRange";
import { useTimeRangeHistory } from "../../react/timeline/time-range-history-utils";
import { SET_TIMESTAMP_MARKER_HOTKEY, useTimestampCursorState } from "../../TimestampCursor";

import { TimelineBrushArea, type TimelineBrushAreaProps } from "./TimelineBrushArea";
import type { TimelineControlsState } from "./TimelineControlsState";
import { TimelineHoverTimestamp } from "./TimelineHoverTimestamp";

export interface TimelineControlsProps {
	state: TimelineControlsState;
	brushAreaOffsetTop?: number;
	brushAreaOffsetBottom?: number;
	hideMarkers: boolean;
	hideHoverTimestamp: boolean;
	onClick?: false | React.Dispatch<Time>;
	onBrush?: false | TimelineBrushAreaProps["onBrush"];
	/**
	 * If `true' the range is saved in the history on brush
	 *
	 * @default true
	 */
	preserveHistory?: boolean;
}

export const TimelineControls: React.FC<TimelineControlsProps> = ({
	brushAreaOffsetTop,
	brushAreaOffsetBottom,
	hideMarkers,
	hideHoverTimestamp,
	state,
	onClick,
	onBrush,
	preserveHistory = true,
}) => {
	const scope = useScope();
	const timeRangeHistory = useTimeRangeHistory();
	const timestampCursorState = useTimestampCursorState();
	const isTimestampCursorHotkeyPressed = !hideMarkers && timestampCursorState.isHotkeyPressed(scope);

	const timelineBrushAreaState = useScopedMemo((scope) => state.toBrushArea(scope), [state]);

	useKeyPressEvent(
		SET_TIMESTAMP_MARKER_HOTKEY,
		useHandler(() => {
			state.cancelBrushing();
		}),
	);

	useKeyPressEvent(
		"Escape",
		useHandler(() => {
			state.cancelBrushing();
		}),
	);

	if (
		// can't start brushing if hotkey is pressed
		!isTimestampCursorHotkeyPressed &&
		onBrush &&
		state.isBrushing(scope)
	) {
		return (
			<TimelineBrushArea
				areaOffsetBottom={brushAreaOffsetBottom}
				areaOffsetTop={brushAreaOffsetTop}
				onBrush={(left, right, position) => {
					onBrush(left, right, position);
					if (preserveHistory) {
						timeRangeHistory?.setValue((current) => current.add(new SpecificRange(left, right)));
					}
					state.endBrushing();
				}}
				onCancel={() => {
					state.cancelBrushing();
				}}
				state={timelineBrushAreaState}
			/>
		);
	}

	if (state.isHovering(scope) && !hideHoverTimestamp) {
		return (
			<TimelineHoverTimestamp
				disableMarker={hideMarkers}
				onMouseDown={(time, px) => {
					state.startBrushing(time, px);
				}}
				onMouseUp={(time) => {
					batch((scope) => {
						state.endBrushing();

						// the hover timestamp should not trigger click if the brushing was cancelled
						if (state.shouldPreventClick(scope)) {
							return;
						}

						// toggle marker if hotkey is pressed
						if (isTimestampCursorHotkeyPressed) {
							timestampCursorState.toggleMarker(time);
						} else if (onClick) {
							onClick(time);
						}
					});
				}}
				x={state.x}
			/>
		);
	}

	return null;
};
